import "./lexicalUnits.css";
import { IpaType } from "../../phonemes/ipa";
import { useState } from "react";
import { lsUnitForDisplay, phonemeForLength } from "./lsUnit";
import { SetName } from "../../rules/findLexicalSets";
import { cleanupIpa } from "../../phonemes/cleanupIpa";
import { IpaKeyboard } from "../widgets/IpaKeyboard";

/** information about a single lexical unit/set */
export interface LexicalUnit {
  readonly name: SetName;
  readonly phoneme: IpaType;
  readonly length?: number;
}

/** table of lexical sets: name, phoneme, length */
export function LexicalUnits(props: {
  readonly units: LexicalUnit[],
  /** special handling of rhotics */
  readonly nonRhotic?: boolean,
  /** called when set name is clicked on */
  readonly onClick: (u: LexicalUnit) => void,
  /** called when this control changes the phoneme associated with a lexical set */
  readonly onChangePhoneme: (setName: SetName, phoneme: IpaType) => void,
  /** called when this control changes phoneme length */
  readonly onChangeLength: (phoneme: IpaType, length: number) => void,
}) {
  const [selectedSetName, setSelectedSetName] = useState(props.units[0].name);
  const [phonemeSetName, setPhonemeSetName] = useState(''); // name of lexical set for editing phoneme
  const [phoneme, setPhoneme] = useState(''); // phoneme string being edited
  const [lengthSetName, setLengthSetName] = useState(''); // name of lexical set for editing length
  const [length, setLength] = useState(''); // length string being edited

  function onPickUnit(u: LexicalUnit) {
    props.onClick(u);
    setSelectedSetName(u.name);
  }

  function startEditingPhoneme(u: LexicalUnit) {
    setPhonemeSetName(u.name);
    setPhoneme(u.phoneme);
    setSelectedSetName(u.name);
  }

  function onPhonemeKey(u: LexicalUnit, key: string) {
    if (key === 'Enter') {
      // save current phoneme & stop editing
      const withoutParens = phoneme.replace(/\(|\)/g, '');
      const { ipa } = cleanupIpa(withoutParens);
      props.onChangePhoneme(u.name, ipa);
      setPhonemeSetName('');
    } else if (key === 'Escape') {
      // stop editing length, toss current value
      setPhonemeSetName('');
    }
  }

  function startEditingLength(u: LexicalUnit) {
    setLengthSetName(u.name);
    setLength(u.length ? u.length.toString() : '');
    setSelectedSetName(u.name);
  }

  function onLengthKey(u: LexicalUnit, key: string) {
    if (key === 'Enter') {
      // save current length & stop editing
      const len = Number.parseInt(length);
      if (0 < len && len < 1000) {
        props.onChangeLength(u.phoneme, len);
      }
      setLengthSetName('');
    } else if (key === 'Escape') {
      // stop editing length, toss current value
      setLengthSetName('');
    }
  }

  // hilite phonemes that share the same length
  const phonemeToHilite = phonemeForLength(props.units.filter(u => u.name === selectedSetName)[0].phoneme, props.nonRhotic);

  return (<table>
    <thead><tr>
      <th>name</th>
      <th>sound</th>
      <th>len/ms</th>
    </tr></thead>
    <tbody className="unitTable">
      {props.units.map(u => {
        const displayPhoneme = lsUnitForDisplay(u.phoneme, props.nonRhotic);
        const lengthPhoneme = phonemeForLength(u.phoneme, props.nonRhotic);
        const trClass = u.name === selectedSetName ? 'selectedUnit' : undefined;
        const phClass = u.name !== selectedSetName && lengthPhoneme === phonemeToHilite ? 'hilitedUnit' : undefined;
        return (<tr key={u.name} className={trClass}>
          <td onClick={() => onPickUnit(u)}>{u.name}</td>
          <td className={phClass} onClick={() => startEditingPhoneme(u)}>
            {u.name === phonemeSetName
              ? <IpaKeyboard cols={3} value={phoneme} useInput={true}
                onChange={setPhoneme} onKeyDown={(key) => onPhonemeKey(u, key)} />
              : displayPhoneme}
          </td>
          <td className={phClass} onClick={() => startEditingLength(u)}>
            {u.name === lengthSetName
              ? <input size={3} spellCheck={false} value={length} maxLength={3}
                onChange={(e) => setLength(e.target.value)} onKeyDown={(e) => onLengthKey(u, e.key)} />
              : u.length}
          </td>
        </tr>);
      })}
    </tbody>
  </table>);
}
